import { z } from 'zod';
import { uuid } from './schemas.js';

export const PRODUCTIVITY_CLASSES = ['productive', 'unproductive', 'neutral'] as const;
export type ProductivityClass = (typeof PRODUCTIVITY_CLASSES)[number];
export const RULE_MATCH_TYPES = ['app', 'domain', 'title'] as const;
export type RuleMatchType = (typeof RULE_MATCH_TYPES)[number];

export const CLASS_LABELS: Record<ProductivityClass | 'unknown' | 'idle', string> = {
  productive: 'Productive',
  unproductive: 'Unproductive',
  neutral: 'Neutral',
  unknown: 'Not classified',
  idle: 'Idle',
};

/** "C:\\Program Files\\Revit.exe" → "revit.exe" */
export function normalizeApp(name: string | null | undefined): string | null {
  if (!name) return null;
  const base = name.trim().split(/[\\/]/).pop()!.toLowerCase();
  return base || null;
}

/** "https://www.YouTube.com:443/watch" → "youtube.com" */
export function normalizeDomain(d: string | null | undefined): string | null {
  if (!d) return null;
  let s = d.trim().toLowerCase();
  s = s.replace(/^[a-z]+:\/\//, '').split(/[/?#]/)[0]!.split(':')[0]!.replace(/^www\./, '').replace(/\.$/, '');
  return /^[a-z0-9.-]{1,253}$/.test(s) && s.includes('.') ? s : null;
}

export const ruleSchema = z
  .object({
    matchType: z.enum(RULE_MATCH_TYPES),
    pattern: z.string().trim().min(1).max(200),
    label: z.string().trim().max(120).nullable().optional(),
    classification: z.enum(PRODUCTIVITY_CLASSES),
    departmentId: uuid.nullable().optional(),
    /** Exception for one person (wins over department and company rules). */
    employeeId: uuid.nullable().optional(),
    category: z.string().trim().max(60).nullable().optional(),
    note: z.string().trim().max(300).nullable().optional(),
  })
  .strict()
  .transform((r, ctx) => {
    if (r.departmentId && r.employeeId) {
      ctx.addIssue({ code: 'custom', path: ['employeeId'], message: 'Choose a department or one person, not both' });
      return z.NEVER;
    }
    const pattern = r.matchType === 'domain' ? normalizeDomain(r.pattern) : r.matchType === 'app' ? normalizeApp(r.pattern) : r.pattern.toLowerCase();
    if (!pattern) {
      ctx.addIssue({ code: 'custom', path: ['pattern'], message: r.matchType === 'domain' ? 'Enter a website like youtube.com' : 'Enter a name' });
      return z.NEVER;
    }
    return { ...r, pattern };
  });
export type RuleInput = z.infer<typeof ruleSchema>;

export const enrollSchema = z
  .object({
    code: z.string().trim().min(6).max(20),
    hostname: z.string().trim().min(1).max(100),
    os: z.string().trim().max(100).optional(),
    agentVersion: z.string().trim().max(40).optional(),
    timezone: z.string().trim().max(64).optional(),
  })
  .strict();

const isoInstant = z.string().datetime({ offset: true });
export const activityBatchSchema = z
  .object({
    segments: z
      .array(
        z
          .object({
            id: z.string().min(1).max(100),
            start: isoInstant,
            end: isoInstant,
            state: z.enum(['active', 'idle']),
            app: z.string().max(260).optional(),
            appTitle: z.string().max(200).optional(),
            domain: z.string().max(300).optional(),
            title: z.string().max(500).optional(),
          })
          .strict(),
      )
      .max(2000),
    counts: z
      .array(
        z
          .object({
            minute: isoInstant,
            keys: z.number().int().min(0).max(100000),
            clicks: z.number().int().min(0).max(100000),
            scrolls: z.number().int().min(0).max(100000),
            mousePx: z.number().int().min(0).max(100000000),
            injected: z.number().int().min(0).max(100000),
            activeMs: z.number().int().min(0).max(60000),
          })
          .strict(),
      )
      .max(2000)
      .optional(),
  })
  .strict();
export type ActivitySegmentInput = z.infer<typeof activityBatchSchema>['segments'][number];
export type ActivityCountInput = NonNullable<z.infer<typeof activityBatchSchema>['counts']>[number];

/** Metadata the agent sends with each screenshot (the image itself is the request body). */
export const screenshotMetaSchema = z
  .object({
    clientId: z.string().min(1).max(100),
    capturedAt: isoInstant,
    width: z.number().int().positive().max(20000).optional(),
    height: z.number().int().positive().max(20000).optional(),
    blurred: z.boolean().optional(),
  })
  .strict();
export type ScreenshotMetaInput = z.infer<typeof screenshotMetaSchema>;

export interface ScreenshotDto {
  id: string;
  employeeId: string;
  employeeName?: string;
  capturedAt: string;
  width: number | null;
  height: number | null;
  blurred: boolean;
  deleted: boolean;
}

/** A stretch flagged for review: regular machine-like ("injected") pointer movement, or mouse-only activity. Review only — never deducted automatically. */
export interface FlaggedRangeDto {
  from: string;
  to: string;
  minutes: number;
  reason: 'mouse_only' | 'injected';
}

/** What the screenshot endpoints return. `flagged` is present for administrators only. */
export interface ScreenshotListDto {
  screenshots: ScreenshotDto[];
  flagged?: FlaggedRangeDto[];
}

export const consentSchema = z.object({ noticeVersion: z.string().min(4).max(64) }).strict();
export const enrollmentCodeSchema = z.object({ employeeId: uuid }).strict();

export interface ProductivityRuleDto {
  id: string;
  matchType: RuleMatchType;
  pattern: string;
  label: string | null;
  classification: ProductivityClass;
  departmentId: string | null;
  departmentName: string | null;
  employeeId: string | null;
  employeeName: string | null;
  category: string | null;
  note: string | null;
  updatedAt: string;
}

export interface AgentDeviceDto {
  id: string;
  employeeId: string;
  employeeName: string;
  hostname: string;
  os: string | null;
  agentVersion: string | null;
  enrolledAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
}

export interface AppUsageDto {
  key: string;
  label: string;
  kind: 'app' | 'domain';
  classification: ProductivityClass | 'unknown';
  minutes: number;
}

/** Minutes in one hour of the day (hour = local clock hour). */
export interface HourBucketDto {
  hour: number;
  productive: number;
  unproductive: number;
  neutral: number;
  unknown: number;
  idle: number;
}

export interface ProductivityTotalsDto {
  /** Days with any recorded activity. */
  days: number;
  trackedMinutes: number;
  activeMinutes: number;
  productiveMinutes: number;
  unproductiveMinutes: number;
  neutralMinutes: number;
  unknownMinutes: number;
  idleMinutes: number;
  productivityPct: number | null;
}

export interface ProductivityDayDto {
  employeeId: string;
  employeeName?: string;
  date: string;
  timezone: string;
  firstActivity: string | null;
  lastActivity: string | null;
  trackedMinutes: number;
  activeMinutes: number;
  productiveMinutes: number;
  unproductiveMinutes: number;
  neutralMinutes: number;
  unknownMinutes: number;
  idleMinutes: number;
  /** Productive share of tracked time (unknown counted per setting), 0–100; null when nothing tracked. */
  productivityPct: number | null;
  apps: AppUsageDto[];
  /** 24 hourly buckets for the day's timeline, starting at the attendance day-start hour. */
  hours: HourBucketDto[];
  /** Figures frozen when the reporting period was closed. */
  frozen: boolean;
}

export interface DailyReportRowDto {
  employeeId: string;
  employeeName: string;
  departmentName: string | null;
  attendance: {
    status: string;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    firstIn: string | null;
    lastOut: string | null;
    workedMinutes: number;
    lateMinutes: number;
    leaveFraction: number;
    holidayName: string | null;
    hoursSource: 'attendance' | 'computer';
  } | null;
  /** null when the person has no computer connected. */
  productivity: ProductivityDayDto | null;
}

export interface DailyReportDto {
  date: string;
  targetPct: number;
  rows: DailyReportRowDto[];
  totals: ProductivityTotalsDto;
}

export interface ProductivitySummaryDto {
  from: string;
  to: string;
  targetPct: number;
  totals: ProductivityTotalsDto;
  employees: {
    employeeId: string;
    employeeName: string;
    departmentName: string | null;
    totals: ProductivityTotalsDto;
    daily: { date: string; productivityPct: number | null; trackedMinutes: number }[];
  }[];
  /** Whole team per day. */
  daily: (ProductivityTotalsDto & { date: string })[];
  /** Whole team by hour of day. */
  hours: HourBucketDto[];
  /** Most-used apps and websites, all classes. */
  topApps: (AppUsageDto & { employees: number })[];
}

export interface UnclassifiedDto {
  matchType: 'app' | 'domain';
  pattern: string;
  label: string;
  minutes: number;
  employees: number;
  lastSeen: string;
}

export interface PrivacyInfoDto {
  enabled: boolean;
  trackWhen: 'clocked_in' | 'schedule' | 'always';
  /** 'computer' = this person's working hours come from the agent (no clocking in). */
  hoursSource: 'attendance' | 'computer';
  idleMinutes: number;
  captureDomains: boolean;
  captureWindowTitles: boolean;
  retentionDays: number;
  /** Whether the company currently takes screenshots (changes the notice and forces re-consent). */
  screenshots: boolean;
  /** How long screenshots are kept, in days. */
  screenshotRetentionDays: number;
  noticeText: string;
  noticeVersion: string;
  accepted: { version: string; at: string } | null;
  devices: { id: string; hostname: string; lastSeenAt: string | null; revokedAt: string | null }[];
}
